import { MapDataService } from '@ui/shared/domain-map';
import { map, type Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { SportAtlasService, SportsFacilityOrderingEnum } from '@ui/shared/util-api';
import { toRequestQuery } from '@ui/shared/util-filter';
import { SportMapItem } from '../../definitions/sport-map-item';
import type { SportMapFilter } from '../../types/sport-map-filter';
import { LinkedMapItem } from '../../definitions/linked-map-item';
import { TypeFilterOptions } from '../../definitions/filter-option-keys';

@Injectable({ providedIn: 'root' })
export class SportsFacilityMapDataService extends MapDataService<SportMapItem, SportMapFilter> {
    protected _api = inject(SportAtlasService);

    public getData$(filter: SportMapFilter): Observable<Array<SportMapItem>> {
        const typeId = toRequestQuery(filter.types)[0];

        if ((typeId && ![TypeFilterOptions.SportFacility, TypeFilterOptions.All].includes(typeId))) {
            return of([]);
        }

        return this._api.getAllSportsFacilities({
            nameContains: typeof filter.search === 'string' ? filter.search : null,
            sportsIds: toRequestQuery(filter.sports),
            accessibilityFeatureIds: toRequestQuery(filter.accessibilityFeatures),
            order: [SportsFacilityOrderingEnum.Name],
        })
            .pipe(map(response => response.data.map(dto => new SportMapItem({
                sportsFacility: dto,
                linkedItems: this._groupSportsById(
                    dto.sports_club_sports
                        .map(club => new LinkedMapItem({
                            name: club.sports_club.name,
                            id: club.sports_club.id,
                            type: TypeFilterOptions.SportClub,
                            sports: club.sports.map(sport => sport.name),
                        })),
                ),
            }))));
    }

    private _groupSportsById(list: Array<LinkedMapItem>): Array<LinkedMapItem> {
        return list.reduce(
            (newList, listEntry) => {
                const existing = newList.find(entry => entry.id === listEntry.id);

                if (existing) {
                    existing.sports.push(...listEntry.sports);
                } else {
                    newList.push(listEntry);
                }

                return newList;
            },
            new Array<LinkedMapItem>(),
        );
    }
}
